import type { ProjectFormData, SelectedDocument, ProductType } from '@/types'
import { isProductType, getProduct } from '@/config/products'

/**
 * Map product type to the exact value of the HubSpot "product type" dropdown.
 * The HubSpot option internal values are "Structural Floor" / "Underlayment" /
 * "Structural Wall" (NOT the display name with "Panel"), so we send
 * `hubspotValue` from the registry — otherwise HubSpot drops the field.
 */
function mapProductType(productType: ProductType | undefined): string {
  return isProductType(productType) ? getProduct(productType).hubspotValue : ''
}

/**
 * Map status object to comma-separated string format (supports multiple values)
 * Examples:
 * - Multiple: "For Review, For Approval"
 * - Single: "For Review"
 * - Empty: ""
 */
function mapStatusAction(status: ProjectFormData['status'] | undefined): string {
  if (!status) return ''
  
  const selected: string[] = []
  
  if (status.forReview) selected.push('For Review')
  if (status.forApproval) selected.push('For Approval')
  if (status.forRecord) selected.push('For Record')
  if (status.forInformationOnly) selected.push('For Information Only')
  
  return selected.join(', ')
}

/**
 * Format selected documents array to comma-separated string
 */
function formatDocumentsList(selectedDocuments: SelectedDocument[]): string {
  const sortedDocs = selectedDocuments
    .filter(doc => doc.selected)
    .sort((a, b) => a.order - b.order)
  
  return sortedDocs.map(doc => doc.document.name).join(', ')
}

/**
 * Get HubSpot tracking cookie (hutk) from URL parameters or cookies
 */
function getHubSpotCookie(): string | undefined {
  // Try URL parameter first
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search)
    const hutkFromUrl = urlParams.get('hutk')
    if (hutkFromUrl) {
      return hutkFromUrl
    }
    
    // Try cookies
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'hutk' && value) {
        return value
      }
    }
  }
  
  return undefined
}

/**
 * Submit submittal data to HubSpot via Netlify function
 * This is non-blocking and will not throw errors that affect PDF generation
 */
export async function submitSubmittal(
  formData: Partial<ProjectFormData>,
  selectedDocuments: SelectedDocument[]
): Promise<{ success: boolean; error?: string }> {
  try {
    // Prepare data for HubSpot
    const hubspotData = {
      // Contact info
      email: formData.emailAddress || '',
      phone: formData.phoneNumber || '',
      preparedBy: formData.preparedBy || '',
      
      // Submittal info
      submittedTo: formData.submittedTo || '',
      projectName: formData.projectName || '',
      projectNumber: formData.projectNumber || '',
      submittalDate: formData.date || '',
      productType: mapProductType(formData.productType),
      panelThickness: formData.productSize || '',
      statusAction: mapStatusAction(formData.status),
      documentsSelected: formatDocumentsList(selectedDocuments),
      
      // Page context
      pageUri: typeof window !== 'undefined' ? window.location.href : '',
      pageName: typeof document !== 'undefined' ? document.title : 'Submittal Packet Generator',
      
      // HubSpot tracking cookie
      hutk: getHubSpotCookie(),
    }

    // Submit to Netlify function (non-blocking)
    const response = await fetch('/.netlify/functions/submit-submittal', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(hubspotData),
    })

    const result = await response.json()

    if (result.success) {
      console.log('HubSpot submission successful:', result)
      return { success: true }
    } else {
      console.error('HubSpot submission failed:', result)
      return { success: false, error: result.error || 'Unknown error' }
    }
  } catch (error) {
    // Log error but don't throw - PDF generation should continue
    console.error('Error submitting to HubSpot:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    }
  }
}


